import type { UIMessage } from 'ai';
import type { EditorChatMessage } from '@/types/editor';
import { editorToolHistorySummary } from './toolPresentation';

function isGenericCompletionText(value: string): boolean {
  return /^(готово|выполнено)[.!]?$/i.test(value.trim());
}

/**
 * Convert the durable project chat log back into AI SDK messages. Old
 * client-side tool parts cannot be reconstructed safely without their full
 * original inputs and outputs, so completed actions are retained as concise
 * semantic text. This gives the model useful memory after a page reload
 * without risking a replay of an already-applied edit.
 */
export function editorHistoryToUIMessages(history: EditorChatMessage[]): UIMessage[] {
  return history.slice(-60).map((message) => {
    const attachmentNote = message.attachments?.length
      ? `Вложения: ${message.attachments.map((item) => item.name).join(', ')}`
      : '';
    const mentionNote = message.mentions?.length
      ? `Выбраны из медиатеки: ${message.mentions.map((item) => item.title).join(', ')}`
      : '';
    const toolActions = message.role === 'assistant' && message.toolSummaries?.length
      ? editorToolHistorySummary(message.toolSummaries)
      : [];
    const content = toolActions.length > 0 && isGenericCompletionText(message.content)
      ? ''
      : message.content.trim();
    const toolNote = toolActions.length > 0
      ? `Готово\n${toolActions.map((action) => `✓ ${action}`).join('\n')}`
      : '';
    const text = [content, toolNote, mentionNote, attachmentNote].filter(Boolean).join('\n\n').trim();

    return {
      id: message.id,
      role: message.role,
      parts: [{ type: 'text' as const, text }],
    };
  });
}
